//@ts-nocheck

import {createAsyncThunk, createEntityAdapter, createSlice} from '@reduxjs/toolkit'
import {orderInitialState, RefundOrder, RefundOrderState} from '../../models/OrderRefundState'
import * as model from './helper'
import {RootState} from 'setup'
import orderEditService from 'app/services/OrderEditService/OrderEditService'
import * as refund from './actions/refund'
import {getRefundOrder} from './actions/refund'
import {ViewOrder} from '../../models/OrderState'

export const createRefundOrder = createAsyncThunk(
  'order/refund',
  async ({orderId, fulfillmentId}: any, {dispatch}) => {
    try {
      dispatch(setIsFetching(true))
      const data = await orderEditService.createOrderRefund(orderId, fulfillmentId)
      const {orderItems, ...rest} = data
      const items = model.mapToRefundOrderItemModel(orderItems.nodes)
      dispatch(setRefundOrder({...rest, orderItems: items}))
      return items
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(setIsFetching(false))
    }
  }
)

export const updateRefundOrderSummary =
  (orderItems: any[]) => async (dispatch: any, getState: any) => {
    let subtotalPrice: any = model.calculateSubTotalPrice(orderItems)
    if (orderItems.length === 0) {
      subtotalPrice = 0
    }
    await dispatch(setOrderSubtotal(subtotalPrice))
    const totalTax = model.calculateSalesTax(orderItems)
    await dispatch(setOrderSalesTax(totalTax))
    const refund: RefundOrderState = await dispatch(getRefundOrder())
    const lineItems = selectRefundItems(getState())
    if (lineItems.length === 0 && !refund.isRefundAll) {
      dispatch(setIsRefundAll(true))
    } else if (lineItems.length > 0 && refund.isRefundAll) {
      dispatch(setIsRefundAll(false))
    }
  }

export const updateRefundOrder = (lineItems: any[]) => async (dispatch: any) => {
  const data: RefundOrderState = await dispatch(getRefundOrder())
  const {deletedItems} = data
  const orderEdit = model.mapToRefundUpdateModel(data)
  const deletedIds = model.mapDeleteOrderItemModel(lineItems, deletedItems)
  const orderItems = model.mapToOrderEditItemsModel(lineItems)
  await orderEditService.updateRefundOrderEditItems({
    deletedIds,
    orderItems,
    ...orderEdit,
  })
}

export const resetOrderRefundEdit = () => async (dispatch: any, getState: any) => {
  const {refund}: RefundOrderState = await dispatch(getRefundOrder())
  const order = await orderEditService.resetOrderRefundEdit(refund.orderEditId)
  if (order) {
    const {
      orderItems: {nodes: orderItems},
      discounts: {nodes: discounts},
    } = order
    if (orderItems && orderItems.length) {
      const changes = orderItems.map((order: any) => ({
        id: order.variantId,
        changes: {
          id: order.id,
        },
      }))
      if (changes && changes.length) {
        dispatch(updateManyOrderItems(changes))
      }
    }

    dispatch(setUpdateDeleteItems(orderItems))

    if (discounts && discounts?.length) {
      dispatch(setUpdateRefundDiscounts(discounts))
    }
  }
}

export const validateOrderRefund = () => async (dispatch: any) => {
  try {
    dispatch(setIsValidatingDiscount(true))
    const {viewOrder, refund, deletedItems}: RefundOrderState = await dispatch(getRefundOrder())
    if (deletedItems.length > 0) {
      const data = await orderEditService.validateOrderEditDiscount({
        orderEditId: refund.orderEditId,
        lineItems: deletedItems,
        customerId: viewOrder.customerId,
      })
      dispatch(setRefundDiscounts(data.discounts))
    } else {
      dispatch(resetRefundDiscount())
    }
  } finally {
    dispatch(setIsValidatingDiscount(false))
  }
}

const orderAdapter = createEntityAdapter({selectId: (order: any) => order.variantId})
export const {selectAll: selectRefundItems} = orderAdapter.getSelectors(
  ({orders}: any) => orders.orderRefund
)

const refundSlice = createSlice({
  name: 'refund',
  initialState: orderAdapter.getInitialState({...orderInitialState}),
  reducers: {
    setIsFetching: (state, action) => {
      state.isFetching = action.payload
    },
    setIsDirty: (state, action) => {
      state.isDirty = action.payload
    },
    setErrors: (state, action) => {
      state.errors = action.payload
    },
    setOrderErrors: (state, action) => {
      state.orderProcessErrors = action.payload
    },
    setRefundAll: (state, action) => {
      state.isRefundAll = true
      state.deletedItems = [...state.deletedItems, ...action.payload]
      state.isDirty = true
      state.refund.orderRefundPrice = 0
    },
    setDeletedItems: (state, action) => {
      refund.setDeleteItems(state, action.payload)
    },
    setIsRestock: (state, action) => {
      if (!action.payload) {
        state.restockLocationId = null
      }
      state.isRestock = action.payload
    },
    setRestockLocationId: (state, action) => {
      state.restockLocationId = action.payload
    },
    addOrderItem: orderAdapter.addOne,
    updateOrderItem: orderAdapter.updateOne,
    removeOrderItem: orderAdapter.removeOne,
    setOrderItems: orderAdapter.setAll,
    updateManyOrderItems: orderAdapter.updateMany,
    setIsValidatingDiscount: (state, action) => refund.setIsValidatingDiscount(state, action),
    setShippingRefundPrice: (state, action) => refund.setShippingRefundPrice(state, action),
    setOrderRefundPrice: (state, action) => refund.setOrderRefundPrice(state, action),
    setRefundOrder: (state, action) => refund.setRefundOrder(state, action),
    setRefundNote: (state, action) => refund.setRefundNote(state, action),
    setOrderSubtotal: (state, action) => refund.setOrderSubtotal(state, action),
    setOrderSalesTax: (state, action) => refund.setOrderSalesTax(state, action),
    setOrderNote: (state, action) => refund.setOrderNote(state, action),
    setRefundDiscounts: (state, action) => refund.setRefundDiscounts(state, action),
    resetOrderRefundState: () => orderAdapter.getInitialState(orderInitialState),
    resetRefundDiscount: (state) => refund.resetRefundDiscount(state),
    setUpdateDeleteItems: (state, action) => refund.setUpdateDeleteItems(state, action),
    setUpdateRefundDiscounts: (state, action) => refund.setUpdateRefundDiscounts(state, action),
    setIsRefundAll: (state, action) => {
      state.isRefundAll = action.payload
    },
  },
  extraReducers: {
    [createRefundOrder.fulfilled.toString()]: orderAdapter.setAll,
  },
})

export const {
  setIsFetching,
  setOrderItems,
  updateOrderItem,
  removeOrderItem,
  setIsDirty,
  setDeletedItems,
  resetOrderRefundState,
  setOrderNote,
  setOrderSubtotal,
  setErrors,
  addOrderItem,
  setRefundNote,
  setRefundOrder,
  setShippingRefundPrice,
  setIsValidatingDiscount,
  setOrderErrors,
  setRefundDiscounts,
  setRefundAll,
  setIsRestock,
  setRestockLocationId,
  setOrderRefundPrice,
  setOrderSalesTax,
  resetRefundDiscount,
  updateManyOrderItems,
  setUpdateDeleteItems,
  setUpdateRefundDiscounts,
  setIsRefundAll,
} = refundSlice.actions

export default refundSlice.reducer

export const selectViewOrder = ({orders}: RootState): ViewOrder => orders.orderRefund.viewOrder
export const selectViewOrderNote = ({orders}: RootState): string =>
  orders.orderRefund.viewOrder.note
export const selectViewOrderPaymentMethod = ({orders}: RootState): any =>
  orders.orderRefund.viewOrder.paymentMethod
export const selectViewOrderNotes = ({orders}: RootState): any[] =>
  orders.orderRefund.viewOrder.notes
export const selectTransactionPaymentType = ({orders}: RootState): string =>
  orders.orderRefund.viewOrder.paymentType

export const selectRefund = ({orders}: RootState): RefundOrder => orders.orderRefund.refund
export const selectIsFetching = ({orders}: RootState): boolean => orders.orderRefund.isFetching
export const selectIsDirty = ({orders}: RootState): boolean => orders.orderRefund.isDirty

export const selectDeletedItems = ({orders}: RootState): any[] => orders.orderRefund.deletedItems
export const selectIsValidatingDiscount = ({orders}: RootState): boolean =>
  orders.orderRefund.isValidatingDiscount
export const selectOrderDiscounts = ({orders}: RootState): any => orders.orderRefund.discounts

export const selectOrderErrors = ({orders}: RootState): any[] => orders.orderRefund.errors
export const selectOrderProcessErrors = ({orders}: RootState): any[] =>
  orders.orderRefund.orderProcessErrors

export const selectIsRestock = ({orders}: RootState): boolean => orders.orderRefund.isRestock

export const selectRestockLocationId = ({orders}: RootState): string =>
  orders.orderRefund.refund.restockLocationId

export const selectOrderRefundPrice = ({orders}: RootState): number | any =>
  orders.orderRefund.refund.orderRefundPrice
